/**
 * 客户管理 API
 *
 * @description 提供客户相关的 CRUD 操作和业务逻辑接口
 */

import { request } from '@umijs/max';
import type { Project } from './projects';

/**
 * 客户信息
 */
export interface Client {
  /** 数字 ID */
  id?: number;
  /** 业务唯一标识 */
  uuid: string;
  /** 客户公司名称 */
  companyName: string;
  /** 所属行业 */
  industry?: string;
  /** 客户来源（数据字典） */
  source?: string;
  /** 是否已开通轻猎头渠道 */
  hasLightHunterChannel?: boolean;
  /** 发展阶段（数据字典） */
  developmentStage?: string;
  /** 企业性质（数据字典） */
  enterpriseNature?: string;
  /** 公司类型（数据字典） */
  companyType?: string;
  /** 是否同行 */
  isPeer?: boolean;
  /** 公司规模 */
  scale?: string;
  /** 客户等级 */
  level?: string;
  /** 客户状态 */
  status?: string;
  /** 城市（数据字典编码或名称） */
  city?: string;
  /** 城市原始值（省市区 ID 的 JSON，供编辑页级联回填） */
  cityId?: string | null;
  /** 所在城市 */
  location?: string;
  /** 公司网站 */
  website?: string;
  /** 成立时间 */
  foundedAt?: string;
  /** 电话 */
  telephone?: string;
  /** 邮箱 */
  email?: string;
  /** 关联项目数量 */
  projects?: number;
  /** 进展中项目数量（排除已完成/已取消） */
  inProgressProjectCount?: number;
  /** 联系人数量 */
  contactCount?: number;
  /** 累计营收 */
  revenue?: number;
  /** 标签 */
  tags?: string[];
  /** 归属顾问 UUID */
  ownerUuid?: string;
  /** 归属顾问名称 */
  ownerName?: string;
  /** 全部权限（唯一权限字段） */
  hasAllPermission?: boolean;
  /** 最近联系时间 */
  lastContactAt?: string;
  /** 更新时间 */
  updatedAt?: string;
  /** 创建时间 */
  createdAt?: string;
  /** 公司描述 */
  description?: string;
  /** 是否为企业黑名单客户 */
  isBlacklisted?: boolean;
  /** 企业黑名单理由 */
  blacklistReason?: string | null;
  /** 企业拉黑时间 */
  blacklistedTime?: string | number | null;
  /** 企业拉黑用户标识 */
  blacklistedBy?: string | null;
  /** 最新纪要（customer.latestRemark） */
  latestRemark?: string;
  /** 备注 */
  notes?: string;
  /** 营业收入 */
  businessRevenue?: string;
  /** 融资状况 */
  financingStatus?: string;
  /** 公司概况 */
  companyOverview?: string;
  /** 人员规模描述 */
  peopleScaleDescription?: string;
  /** 组织架构 */
  orgStructure?: string;
  /** 发展战略 */
  developmentStrategy?: string;
  /** 发展前景 */
  developmentProspect?: string;
  /** 主要产品 */
  mainProducts?: string;
  /** 主要竞争对手 */
  mainCompetitors?: string;
  /** 竞争对手优劣势 */
  competitorAdvantagesAndDisadvantages?: string;
  /** 雇主品牌亮点 */
  employerBrandHighlights?: string;
  /** 薪酬福利 */
  salaryAndBenefits?: string;
  /** 通勤和环境 */
  commuteAndEnvironment?: string;
  /** 产品和业务目标 */
  productAndBusinessGoals?: string;
}

/**
 * 客户列表查询参数
 */
export interface ClientListParams {
  /** 页码 */
  page?: number;
  /** 单页数量 */
  limit?: number;
  /** 是否使用轻量列表字段 */
  lite?: boolean;
  /** 搜索关键词（公司名称等） */
  search?: string;
  /** 客户等级筛选 */
  level?: string;
  /** 状态筛选 */
  status?: string;
  /** 行业筛选 */
  industry?: string;
  /** 城市/地区筛选 */
  city?: string;
  /** 地址/位置筛选 */
  location?: string;
  /** 客户ID（高级搜索） */
  id?: number;
  /** 标签筛选 */
  tags?: string[];
  /** 排序方式 */
  sortBy?: string;
  /** 列表类型（兼容旧接口） */
  type?: 'my' | 'private' | 'public';
}

/**
 * 客户列表响应
 */
export interface ClientListResponse {
  /** 列表数据 */
  data: Client[];
  /** 总数 */
  total: number;
  /** 当前页码 */
  page: number;
  /** 单页数量 */
  limit: number;
}

export interface ClientProgressProjectItem {
  uuid: string;
  name: string;
  status: string;
  updatedAt?: string | null;
}

export interface ClientProgressProjectsResponse {
  total: number;
  inProgressCount: number;
  projects: ClientProgressProjectItem[];
}

/**
 * 客户创建/更新参数
 */
export interface ClientPayload {
  companyName?: string;
  industry?: string;
  source?: string;
  developmentStage?: string;
  enterpriseNature?: string;
  companyType?: string;
  /** 是否同行 */
  isPeer?: boolean;
  scale?: string;
  level?: string;
  status?: string;
  city?: string;
  location?: string;
  website?: string;
  foundedAt?: string;
  tags?: string[];
  description?: string;
  notes?: string;
  businessRevenue?: string;
  financingStatus?: string;
  companyOverview?: string;
  peopleScaleDescription?: string;
  orgStructure?: string;
  developmentStrategy?: string;
  developmentProspect?: string;
  mainProducts?: string;
  mainCompetitors?: string;
  competitorAdvantagesAndDisadvantages?: string;
  employerBrandHighlights?: string;
  salaryAndBenefits?: string;
  commuteAndEnvironment?: string;
  productAndBusinessGoals?: string;
  /** 所有者 UUID（显式传入时进入私池；不传则进入公海） */
  ownerUuid?: string;
}

export interface ClientPrivatePoolQuotaResponse {
  currentCount: number;
  limit: number;
  isUnlimited: boolean;
  reached: boolean;
}

export interface AiAutofillCompanyProfileRequest {
  companyName: string;
  website?: string;
  location?: string;
  description?: string;
  autoCreateDictionary?: boolean;
  dictionaryCreateConfidenceThreshold?: number;
}

export interface AiAutofillCompanyProfileResponse {
  suggestedFields: Record<string, string | number | null>;
  fieldEvidence: Record<
    string,
    {
      evidence: string;
      confidence: number;
      sourceUrls?: string[];
    }
  >;
  sources: Array<{ title: string; url: string }>;
  dictionaryAutoCreated?: Array<{
    field: string;
    code: string;
    label: string;
    value: string;
    itemUuid: string;
  }>;
  dictionaryResolved?: Array<{
    field: string;
    code: string;
    value: string;
    matchedBy: 'existing' | 'created';
  }>;
  dictionarySkipped?: Array<{ field: string; reason: string }>;
  searchMeta?: {
    attempted?: boolean;
    enabled?: boolean;
    success?: boolean;
    fallbackUsed?: boolean;
    retryCount?: number;
    failureReason?: string;
    sourceCount?: number;
  };
  networkStatus: 'success' | 'degraded';
  uncertaintyNote?: string;
}

/**
 * 联系人信息
 */
export interface Contact {
  /** 数字 ID */
  id?: number;
  /** 业务唯一标识 */
  uuid?: string;
  /** 姓名 */
  name: string;
  /** 职位 */
  position?: string;
  /** 电话 */
  phone?: string;
  /** 座机 */
  telephone?: string;
  /** 邮箱 */
  email?: string;
  /** 微信 */
  wechat?: string;
  /** 性别 */
  sex?: string;
  /** 生日 */
  birthday?: string;
  /** 地址 */
  address?: string;
  /** 备注 */
  remarks?: string;
  notes?: string;
  /** 是否主要联系人 */
  isPrimary?: boolean;
  important?: boolean;
  /** 下次联系内容 */
  nextContactContent?: string;
  /** 创建时间 */
  createdAt?: string;
  /** 更新时间 */
  updatedAt?: string;
}

/**
 * 沟通记录
 */
export interface ClientCommunication {
  /** 数字 ID */
  id?: number;
  /** 业务唯一标识 */
  uuid?: string;
  /** 沟通类型 */
  type?: string;
  /** 沟通方向 */
  direction?: string;
  /** 联系人 UUID */
  contactUuid?: string;
  /** 联系人 ID */
  contactId?: number;
  /** 联系人信息 */
  contact?: Contact;
  /** 沟通主题 */
  subject?: string;
  /** 沟通内容 */
  content?: string;
  /** 沟通时长（分钟） */
  duration?: number;
  /** 沟通结果 */
  result?: string;
  /** 下一步行动 */
  nextAction?: string;
  nextContactContent?: string;
  /** 下次行动时间 */
  nextActionDate?: string;
  nextContactTime?: string;
  /** 客户情绪 */
  clientMood?: string;
  /** 满意度 */
  satisfaction?: number;
  /** 录音文件云存储链接 */
  audioUrl?: string;
  /** 语音转写文本 */
  transcriptionText?: string;
  /** 录音时长（秒） */
  audioDuration?: number;
  /** 创建时间 */
  createdAt?: string;
  /** 更新时间 */
  updatedAt?: string;
  /** 创建人用户 UUID */
  creator?: string;
  /** 最后修改人用户 UUID */
  modifier?: string;
  createdTime?: string;
  updatedTime?: string | number;
}

/**
 * 竞调记录
 */
export interface ClientCompetitiveResearch {
  /** 业务唯一标识 */
  uuid: string;
  /** 调查日期 */
  researchDate: string;
  /** 竞争对手公司名称 */
  competitorCompany: string;
  /** 竞争对手行业 */
  competitorIndustry?: string;
  /** 竞争对手地址 */
  competitorLocation?: string;
  /** 竞争对手规模 */
  competitorScale?: string;
  /** 竞争对手官网 */
  competitorWebsite?: string;
  /** 招聘状态 */
  hiringStatus?: string;
  /** 竞争对手描述 */
  competitorDescription?: string;
  /** 开放职位 */
  openPositions?: string[];
  /** 薪资范围 */
  salaryRange?: string;
  /** 招聘要求 */
  hiringRequirements?: string;
  /** 竞争优势 */
  competitiveAdvantage?: string;
  /** 竞争劣势 */
  competitiveDisadvantage?: string;
  /** 市场地位 */
  marketPosition?: string;
  /** 威胁等级 */
  threatLevel?: string;
  /** 业务新闻 */
  businessNews?: string;
  /** 近期活动 */
  recentActivities?: string;
  /** 战略重点 */
  strategicFocus?: string;
  /** 人才流动 */
  talentFlow?: string;
  /** 关键人员 */
  keyPersonnel?: string[];
  /** 备注 */
  notes?: string;
}

/**
 * 开票信息
 */
export interface BillingInfo {
  /** 数字 ID */
  id?: number;
  /** 关联客户 ID */
  customerId?: number;
  /** 业务唯一标识 */
  uuid?: string;
  /** 抬头 */
  title: string;
  /** 纳税人识别号 */
  taxNumber?: string;
  /** 地址 */
  address?: string;
  /** 电话 */
  phone?: string;
  /** 开户行 */
  bank?: string;
  /** 账号 */
  account?: string;
  /** 状态 */
  states?: number | null;
  /** 创建时间 */
  createdAt?: string | number;
  createdTime?: string | number;
  /** 更新时间 */
  updatedAt?: string | number;
  updatedTime?: string | number;
}

/**
 * 检查联系人电话返回结果
 */
export interface CheckContactPhoneResult {
  exists: boolean;
  contact?: Contact;
  clientName?: string;
}

/**
 * 检查客户名称返回结果
 */
export interface CheckClientNameResult {
  exists: boolean;
  client?: Pick<Client, 'id' | 'uuid' | 'companyName'>;
}

/**
 * 客户管理 API
 */
export const clientApi = {
  /**
   * 获取客户列表
   *
   * @param params - 查询条件
   */
  getList: (params?: ClientListParams) => {
    const queryParams = { lite: true, ...(params ?? {}) };
    return request<ClientListResponse>('/bff/luole-hunter/clients', {
      params: queryParams,
    });
  },

  /**
   * 查询我创建的客户
   */
  getMyClients: (params?: ClientListParams) => {
    const queryParams = { lite: true, ...(params ?? {}) };
    return request<ClientListResponse>('/bff/luole-hunter/clients/my-clients', {
      params: queryParams,
    });
  },

  /**
   * 查询我私池的客户
   */
  getMyPrivateClients: (params?: ClientListParams) => {
    const queryParams = { lite: true, ...(params ?? {}) };
    return request<ClientListResponse>(
      '/bff/luole-hunter/clients/my-private-clients',
      { params: queryParams },
    );
  },

  /**
   * 查询项目/职位场景可选私池客户（组织架构链条可管理范围）
   */
  getProjectCandidatePrivateClients: (params?: ClientListParams) => {
    const queryParams = { lite: true, ...(params ?? {}) };
    return request<ClientListResponse>(
      '/bff/luole-hunter/clients/project-candidate-private-clients',
      { params: queryParams },
    );
  },

  /**
   * 查询公海客户
   */
  getPublicPoolClients: (params?: ClientListParams) => {
    const queryParams = { lite: true, ...(params ?? {}) };
    return request<ClientListResponse>(
      '/bff/luole-hunter/clients/public-pool-clients',
      { params: queryParams },
    );
  },

  /**
   * 获取客户详情
   *
   * @param uuid - 客户 UUID
   */
  getDetail: (uuid: string) => {
    return request<Client>(`/bff/luole-hunter/clients/${uuid}`);
  },

  /** 获取实际关联项目，与客户预览抽屉使用同一查询范围。 */
  getProjects: (uuid: string) => {
    return request<Project[]>(`/bff/luole-hunter/clients/${uuid}/projects`);
  },

  /**
   * 获取客户关联项目进展列表（用于客户列表“进展中”悬浮展示）
   *
   * @param clientUuid - 客户 UUID
   */
  getProgressProjects: (clientUuid: string) => {
    return request<ClientProgressProjectsResponse>(
      `/bff/luole-hunter/clients/${clientUuid}/progress-projects`,
    );
  },

  /**
   * 获取客户详情（含联系人、沟通记录、竞调、开票信息）- 一次请求加载详情页核心数据
   */
  getDetailWithDetails: (uuid: string) => {
    return request<{
      client: Client;
      contacts: Contact[];
      communications: ClientCommunication[];
      competitiveResearches: ClientCompetitiveResearch[];
      billingInfo: BillingInfo[];
      projects: Project[];
    }>(`/bff/luole-hunter/clients/${uuid}/details`);
  },

  /**
   * 检查客户名称是否已存在
   *
   * @param companyName - 客户名称
   * @param excludeUuid - 排除的客户 UUID（编辑时使用）
   */
  checkNameExists: (companyName: string, excludeUuid?: string) => {
    const params: Record<string, string> = { companyName };
    if (excludeUuid) {
      params.excludeUuid = excludeUuid;
    }
    return request<CheckClientNameResult>(
      '/bff/luole-hunter/clients/check-name',
      {
        params,
        skipErrorHandler: true,
      },
    );
  },

  /**
   * 创建客户
   *
   * @param data - 客户数据
   */
  create: (data: Partial<Client>) => {
    return request<Client>('/bff/luole-hunter/clients', {
      method: 'POST',
      data,
    });
  },

  /**
   * 更新客户
   *
   * @param uuid - 客户 UUID
   * @param data - 更新内容
   */
  update: (uuid: string, data: Partial<Client>) => {
    return request<Client>(`/bff/luole-hunter/clients/${uuid}`, {
      method: 'PATCH',
      data,
    });
  },

  /**
   * 删除客户
   *
   * @param uuid - 客户 UUID
   */
  delete: (uuid: string) => {
    return request<{ message: string }>(`/bff/luole-hunter/clients/${uuid}`, {
      method: 'DELETE',
    });
  },

  /**
   * 批量删除客户
   *
   * @param clientUuids - 客户 UUID 数组
   */
  batchDelete: (clientUuids: string[]) => {
    return request<{
      success: boolean;
      message: string;
      total: number;
      successCount: number;
      failCount: number;
      successList: string[];
      failList: Array<{ uuid: string; reason: string; name?: string }>;
    }>('/bff/luole-hunter/clients/batch-delete', {
      method: 'POST',
      data: { clientUuids },
    });
  },

  /**
   * 加入私池
   *
   * @param uuid - 客户 UUID
   */
  addToPrivatePool: (uuid: string) => {
    return request<{ message: string }>(
      `/bff/luole-hunter/clients/${uuid}/add-to-private-pool`,
      {
        method: 'POST',
        skipErrorHandler: true,
      },
    );
  },

  /**
   * 移除私池
   *
   * @param uuid - 客户 UUID
   */
  removeFromPrivatePool: (uuid: string) => {
    return request<{ message: string }>(
      `/bff/luole-hunter/clients/${uuid}/remove-from-private-pool`,
      {
        method: 'POST',
        skipErrorHandler: true,
      },
    );
  },

  /**
   * 批量加入私池
   *
   * @param clientUuids - 客户 UUID 数组
   */
  batchAddToPrivatePool: (clientUuids: string[]) => {
    return request<{
      success: boolean;
      message: string;
      total: number;
      successCount: number;
      failCount: number;
      successList: string[];
      failList: Array<{ uuid: string; reason: string; companyName?: string }>;
    }>('/bff/luole-hunter/clients/batch-add-to-private-pool', {
      method: 'POST',
      data: { clientUuids },
      skipErrorHandler: true,
    });
  },

  /**
   * 批量移除私池
   *
   * @param clientUuids - 客户 UUID 数组
   */
  batchRemoveFromPrivatePool: (clientUuids: string[]) => {
    return request<{
      success: boolean;
      message: string;
      total: number;
      successCount: number;
      failCount: number;
      successList: string[];
      failList: Array<{ uuid: string; reason: string; companyName?: string }>;
    }>('/bff/luole-hunter/clients/batch-remove-from-private-pool', {
      method: 'POST',
      data: { clientUuids },
      skipErrorHandler: true,
    });
  },

  /**
   * 获取当前登录人客户私池上限及占用情况
   */
  getPrivatePoolQuota: () => {
    return request<ClientPrivatePoolQuotaResponse>(
      '/bff/luole-hunter/clients/private-pool-quota',
    );
  },

  // ==================== 联系人相关 ====================

  /**
   * 获取客户联系人列表
   *
   * @param clientUuid - 客户 UUID
   */
  getContacts: (clientUuid: string) => {
    return request<Contact[]>(
      `/bff/luole-hunter/clients/${clientUuid}/contacts`,
    );
  },

  /**
   * 创建联系人
   *
   * @param clientUuid - 客户 UUID
   * @param data - 联系人数据
   */
  createContact: (clientUuid: string, data: Partial<Contact>) => {
    return request<Contact>(
      `/bff/luole-hunter/clients/${clientUuid}/contacts`,
      {
        method: 'POST',
        data,
      },
    );
  },

  /**
   * 更新联系人
   *
   * @param contactId - 联系人 ID
   * @param data - 更新内容
   */
  updateContact: (contactId: number, data: Partial<Contact>) => {
    return request<Contact>(`/bff/luole-hunter/clients/contacts/${contactId}`, {
      method: 'PATCH',
      data,
    });
  },

  /**
   * 删除联系人关联
   *
   * @param clientUuid - 客户 UUID
   * @param contactId - 联系人 ID
   */
  deleteContact: (clientUuid: string, contactId: number) => {
    return request<{ message: string }>(
      `/bff/luole-hunter/clients/${clientUuid}/contacts/${contactId}`,
      {
        method: 'DELETE',
      },
    );
  },

  /**
   * 设置主要联系人
   *
   * @param clientUuid - 客户 UUID
   * @param contactUuid - 联系人 UUID
   */
  setPrimaryContact: (clientUuid: string, contactUuid: string) => {
    return request<Contact>(
      `/bff/luole-hunter/clients/${clientUuid}/contacts/${contactUuid}/set-primary`,
      {
        method: 'POST',
      },
    );
  },

  /**
   * 关联联系人到客户
   *
   * @param clientUuid - 客户 UUID
   * @param contactId - 联系人 ID
   */
  associateContactToClient: (clientUuid: string, contactId: number) => {
    return request<Contact>(
      `/bff/luole-hunter/clients/${clientUuid}/associate-contact/${contactId}`,
      {
        method: 'POST',
      },
    );
  },

  /**
   * 检查联系人电话是否已存在
   *
   * @param phone - 电话号码
   * @param excludeContactId - 排除的联系人 ID（编辑时使用）
   */
  checkContactPhoneExists: (phone: string, excludeContactId?: number) => {
    const params: Record<string, string> = { phone };
    if (excludeContactId !== undefined) {
      params.excludeId = String(excludeContactId);
    }
    return request<CheckContactPhoneResult>(
      '/bff/luole-hunter/clients/contacts/check-phone',
      {
        params,
      },
    );
  },

  // ==================== 沟通记录相关 ====================

  /**
   * 获取客户沟通记录列表
   *
   * @param clientUuid - 客户 UUID
   */
  getCommunications: (clientUuid: string) => {
    return request<ClientCommunication[]>(
      `/bff/luole-hunter/clients/${clientUuid}/communications`,
    );
  },

  /**
   * 分页获取客户沟通记录列表
   *
   * @param clientUuid - 客户 UUID
   * @param params - 分页参数
   */
  getCommunicationsPaged: (
    clientUuid: string,
    params?: { page?: number; limit?: number },
  ) => {
    return request<{
      data: ClientCommunication[];
      total: number;
      page: number;
      limit: number;
    }>(`/bff/luole-hunter/clients/${clientUuid}/communications`, { params });
  },

  /**
   * 创建沟通记录
   *
   * @param clientUuid - 客户 UUID
   * @param data - 沟通记录数据
   */
  createCommunication: (
    clientUuid: string,
    data: Partial<ClientCommunication>,
  ) => {
    return request<ClientCommunication>(
      `/bff/luole-hunter/clients/${clientUuid}/communications`,
      {
        method: 'POST',
        data,
      },
    );
  },

  /**
   * 更新沟通记录
   *
   * @param recordId - 记录 ID
   * @param data - 更新内容
   */
  updateCommunication: (
    recordId: number,
    data: Partial<ClientCommunication>,
  ) => {
    return request<ClientCommunication>(
      `/bff/luole-hunter/clients/communications/${recordId}`,
      {
        method: 'PATCH',
        data,
      },
    );
  },

  /**
   * 删除沟通记录
   *
   * @param recordId - 记录 ID
   */
  deleteCommunication: (recordId: number) => {
    return request<{ message: string }>(
      `/bff/luole-hunter/clients/communications/${recordId}`,
      {
        method: 'DELETE',
      },
    );
  },

  /**
   * AI 生成沟通摘要并提炼要点（用于录音转写后生成沟通内容）
   *
   * @param text - 沟通内容/录音转写文本
   */
  generateCommunicationSummary: (text: string) => {
    return request<{ summary: string; keyPoints: string[] }>(
      '/bff/luole-hunter/clients/actions/generate-communication-summary',
      {
        method: 'POST',
        data: { text },
      },
    );
  },

  /**
   * AI 自动补全企业信息（联网检索优先）
   */
  aiAutofillCompanyProfile: (data: AiAutofillCompanyProfileRequest) => {
    return request<AiAutofillCompanyProfileResponse>(
      '/bff/luole-hunter/clients/actions/ai-autofill-company-profile',
      {
        method: 'POST',
        data,
      },
    );
  },

  /**
   * 上传沟通录音：云存储 + 语音转写 + AI 摘要与要点
   *
   * @param file - 录音 Blob（如 webm）
   * @param audioDurationSeconds - 录音时长（秒），可选
   */
  uploadCommunicationAudio: (
    file: Blob,
    audioDurationSeconds?: number,
  ): Promise<{
    audioUrl: string;
    content: string;
    transcriptionText: string;
    keyPoints: string[];
    audioDuration?: number;
  }> => {
    const formData = new FormData();
    formData.append('file', file, `recording_${Date.now()}.webm`);
    if (audioDurationSeconds != null && !Number.isNaN(audioDurationSeconds)) {
      formData.append(
        'audioDuration',
        String(Math.round(audioDurationSeconds)),
      );
    }
    return request(
      '/bff/luole-hunter/clients/actions/upload-communication-audio',
      {
        method: 'POST',
        data: formData,
      },
    );
  },

  // ==================== 竞调记录相关 ====================

  /**
   * 获取客户竞调记录列表
   *
   * @param clientUuid - 客户 UUID
   */
  getCompetitiveResearches: (clientUuid: string) => {
    return request<ClientCompetitiveResearch[]>(
      `/bff/luole-hunter/clients/${clientUuid}/competitive-researches`,
    );
  },

  /**
   * 创建竞调记录
   *
   * @param clientUuid - 客户 UUID
   * @param data - 竞调记录数据
   */
  createCompetitiveResearch: (
    clientUuid: string,
    data: Partial<ClientCompetitiveResearch>,
  ) => {
    return request<ClientCompetitiveResearch>(
      `/bff/luole-hunter/clients/${clientUuid}/competitive-researches`,
      {
        method: 'POST',
        data,
      },
    );
  },

  /**
   * 更新竞调记录
   *
   * @param researchUuid - 竞调记录 UUID
   * @param data - 更新内容
   */
  updateCompetitiveResearch: (
    researchUuid: string,
    data: Partial<ClientCompetitiveResearch>,
  ) => {
    return request<ClientCompetitiveResearch>(
      `/bff/luole-hunter/clients/competitive-researches/${researchUuid}`,
      {
        method: 'PATCH',
        data,
      },
    );
  },

  /**
   * 删除竞调记录
   *
   * @param researchUuid - 竞调记录 UUID
   */
  deleteCompetitiveResearch: (researchUuid: string) => {
    return request<{ message: string }>(
      `/bff/luole-hunter/clients/competitive-researches/${researchUuid}`,
      {
        method: 'DELETE',
      },
    );
  },

  // ==================== 开票信息相关 ====================

  /**
   * 获取客户开票信息列表
   *
   * @param clientUuid - 客户 UUID
   */
  getBillingInfo: (clientUuid: string) => {
    return request<BillingInfo[]>(
      `/bff/luole-hunter/clients/${clientUuid}/billing-info`,
      {
        skipErrorHandler: true,
      },
    );
  },

  /**
   * 创建开票信息
   *
   * @param clientUuid - 客户 UUID
   * @param data - 开票信息数据
   */
  createBillingInfo: (clientUuid: string, data: Partial<BillingInfo>) => {
    return request<BillingInfo>(
      `/bff/luole-hunter/clients/${clientUuid}/billing-info`,
      {
        method: 'POST',
        data,
      },
    );
  },

  /**
   * 更新开票信息
   *
   * @param billingInfoId - 开票信息 ID
   * @param data - 更新内容
   */
  updateBillingInfo: (billingInfoId: number, data: Partial<BillingInfo>) => {
    return request<BillingInfo>(
      `/bff/luole-hunter/clients/billing-info/${billingInfoId}`,
      {
        method: 'PATCH',
        data,
      },
    );
  },

  /**
   * 删除开票信息
   *
   * @param billingInfoId - 开票信息 ID
   */
  deleteBillingInfo: (billingInfoId: number) => {
    return request<{ message: string }>(
      `/bff/luole-hunter/clients/billing-info/${billingInfoId}`,
      {
        method: 'DELETE',
      },
    );
  },

  // ==================== 行业管理 ====================

  /**
   * 获取行业列表
   *
   * @description 从 dict_industry 表获取所有行业数据
   * @returns 行业列表，包含 id 和 name 字段
   */
  getIndustries: () => {
    return request<Array<{ id: string; name: string }>>(
      '/bff/luole-hunter/clients/industries',
    );
  },

  /**
   * 获取客户分类数量统计
   *
   * @description 使用优化的统计接口，仅执行 COUNT 查询，避免复杂 JOIN，大幅提升性能
   * @returns 各分类的客户数量
   */
  getClientCounts: async () => {
    try {
      const response = await request<{
        all: number;
        my: number;
        private: number;
        public: number;
      }>('/bff/luole-hunter/clients/counts');

      return {
        all: response.all || 0,
        my: response.my || 0,
        private: response.private || 0,
        public: response.public || 0,
      };
    } catch (error) {
      console.error('Failed to get client counts:', error);
      return {
        all: 0,
        my: 0,
        private: 0,
        public: 0,
      };
    }
  },

  /**
   * 合并客户
   *
   * @param targetClientId - 目标客户ID（保留的客户）
   * @param sourceClientIds - 源客户ID列表（将被合并后删除）
   * @param fieldOverrides - 字段覆盖配置
   * @returns 合并结果
   */
  mergeClients: async (
    targetClientId: number,
    sourceClientIds: number[],
    fieldOverrides?: Record<string, any>,
  ) => {
    const response = await request<{
      success: boolean;
      message: string;
      mergedData: {
        billingInfos: number;
        customerContacts: number;
        relCustomerContacts: number;
        contactRecords: number;
        clientContacts: number;
        communications: number;
        competitiveResearches: number;
        projects: number;
        collectionClients: number;
      };
      deletedSourceClients: number[];
    }>('/bff/luole-hunter/clients/merge', {
      method: 'POST',
      data: {
        targetClientId,
        sourceClientIds,
        fieldOverrides,
      },
    });
    return response;
  },
};
